import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Download } from "lucide-react";
import { RESUME_URL } from "../../data/services";

const HERO_IMG =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80";

const LINES = ["ENGINEERING", "THE FUTURE", "OF .NET"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      duration: 0.9,
      delay: 0.15 + i * 0.12,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-32 pb-16 bg-[#f4f3ee]"
    >
      <div className="absolute inset-0 grid-lines opacity-70" />

      <div className="max-w-7xl mx-auto w-full px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left — content */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 mb-8"
            >
              <span className="h-px w-12 bg-[#2f4bff]" />
              <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#2f4bff]">
                Shawn Rye — Senior Software Engineer / Architect
              </span>
            </motion.div>

            <h1 className="font-head font-extrabold tracking-tight leading-[0.9] text-[#111111] text-4xl sm:text-6xl md:text-7xl lg:text-8xl break-words">
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span
                    custom={i}
                    variants={lineVariants}
                    initial="hidden"
                    animate="visible"
                    className="block"
                  >
                    {line === "OF .NET" ? (
                      <>
                        OF <span className="text-[#2f4bff]">.NET</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-10"
            >
              <p className="font-body text-lg md:text-xl text-neutral-600 max-w-xl leading-relaxed">
                13+ years designing and modernizing enterprise .NET, Blazor,
                RESTful APIs and Azure applications — with modern AI-assisted
                engineering. Open to new roles and contract work.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="group inline-flex items-center gap-2 bg-[#2f4bff] text-white font-mono-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:bg-[#111111] transition-colors duration-300"
                >
                  Contact me
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 border border-black/20 text-[#111111] font-mono-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:border-[#2f4bff] hover:text-[#2f4bff] transition-colors duration-300"
                >
                  View work
                </Link>
                {/* Résumé download temporarily hidden — restore by uncommenting.
                <a
                  href={RESUME_URL}
                  download
                  data-testid="hero-resume-download"
                  className="group inline-flex items-center gap-2 border border-black/20 text-[#111111] font-mono-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:border-[#2f4bff] hover:text-[#2f4bff] transition-colors duration-300"
                >
                  <Download className="h-4 w-4" />
                  Résumé
                </a>
                */}
              </div>
            </motion.div>
          </div>

          {/* Right — clipped image frame with parallax */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 hidden lg:block"
          >
            <div className="relative h-[520px] w-full overflow-hidden border border-black/10 bg-[#111111]">
              <motion.img
                style={{ y: imgY }}
                src={HERO_IMG}
                alt="Modern architecture lines"
                className="absolute inset-0 h-[120%] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#2f4bff] bg-white px-2 py-1 inline-block">
                  13+ yrs / .NET 10
                </p>
                <p className="mt-3 font-head text-xl font-semibold text-white">
                  Architecture that scales.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.button
          type="button"
          data-testid="scroll-to-explore"
          onClick={() => {
            const el = document.getElementById("work");
            if (window.__lenis && el) {
              window.__lenis.scrollTo(el, { offset: -80 });
            } else if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
              window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
            }
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="group mt-16 flex items-center gap-3 text-neutral-500 hover:text-[#2f4bff] font-mono-tech text-xs uppercase tracking-[0.2em] transition-colors cursor-pointer"
          aria-label="Scroll to explore"
        >
          <ArrowDown className="h-4 w-4 animate-bounce group-hover:text-[#2f4bff]" />
          Scroll to explore
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
